// Regulatory intelligence (APP-004 R3 P4, UI-P6).
//
// Types are transcribed from the frozen Policy AI contract. Three boundaries
// matter more here than anywhere else in the governance surface:
//
//  * Regulation text is tenant owned. Odingard never authors it, and this
//    module only carries what a tenant registered. Nothing in the browser may
//    put a section body into telemetry, analytics or a model prompt.
//  * Diffs and impact classification are deterministic functions of the
//    section hashes. An `ai_assisted` assessment differs from a deterministic
//    one only in its narrative `summary`; `impact`, `confidence` and every
//    verification state come from the same deterministic pass either way, so
//    confidence must never be presented as a reason a status changed.
//  * Verification is human and append only. Items start `unverified`, the
//    author of an assessment may not verify its own items, and an accepted or
//    dismissed assessment is terminal. All three are backend rules; asking and
//    surfacing the answer is this module's whole job.
import { GovernanceHttp, queryString } from "./governanceHttp";
import type { GovernanceClientOptions } from "./governanceHttp";

/** How a derived record was produced. AI only ever writes the narrative. */
export type RegulatoryAnalysisMethod = "deterministic" | "ai_assisted";

export type RegulationVersionStatus = "draft" | "active" | "superseded" | "retired";
export type RegulationSourceKind = "customer_document" | "public_reference" | "odingard_summary";
export type RegulationChangeKind = "added" | "removed" | "modified";

export type ImpactAssessmentStatus = "draft" | "in_review" | "accepted" | "dismissed";
export type ImpactAssetType =
  | "policy"
  | "policy_version"
  | "requirement"
  | "mapping"
  | "evidence"
  | "exception";
export type ImpactLevel = "none" | "review" | "update_required" | "new_control_required";
export type ImpactVerificationState = "unverified" | "verified" | "rejected";
export type ImpactVerificationDecision = "verified" | "rejected" | "reopened";

export type RemediationSourceType = "impact_item" | "finding" | "evidence_request";
export type RemediationStatus = "open" | "in_progress" | "done" | "cancelled";

export interface Regulation {
  id: string;
  organizationId: string;
  regulationKey: string;
  name: string;
  jurisdiction: string;
  authority: string;
  description: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface RegulationVersion {
  id: string;
  organizationId: string;
  regulationId: string;
  versionLabel: string;
  status: RegulationVersionStatus;
  effectiveDate: string | null;
  publishedDate: string | null;
  /** Hash over every section hash. Two identical imports produce one hash. */
  contentHash: string;
  sectionCount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface RegulationSection {
  id: string;
  organizationId: string;
  regulationVersionId: string;
  sectionKey: string;
  title: string;
  body: string;
  sortOrder: number;
  contentHash: string;
  createdBy: string;
  createdAt: string;
}

export interface RegulationSource {
  id: string;
  organizationId: string;
  regulationVersionId: string;
  sourceKind: RegulationSourceKind;
  /** Documents remain the only file path: bytes never reach Policy AI. */
  documentId: string | null;
  referenceUrl: string | null;
  citation: string;
  contentHash: string;
  createdBy: string;
  createdAt: string;
}

export interface RegulationChange {
  sectionKey: string;
  kind: RegulationChangeKind;
  title: string;
  fromHash: string | null;
  toHash: string | null;
  addedTerms: string[];
  removedTerms: string[];
}

export interface RegulationDiff {
  id: string;
  organizationId: string;
  regulationId: string;
  fromVersionId: string;
  toVersionId: string;
  method: RegulatoryAnalysisMethod;
  summary: string;
  changes: RegulationChange[];
  addedCount: number;
  removedCount: number;
  modifiedCount: number;
  model: string;
  analyzerVersion: string;
  /** Keyed on both content hashes: the same pair re-diffs to the same row. */
  inputsHash: string;
  createdBy: string;
  createdAt: string;
}

export interface ImpactCitation {
  sourceType: string;
  sourceId: string;
  excerpt: string;
}

export interface ImpactItem {
  id: string;
  organizationId: string;
  assessmentId: string;
  assetType: ImpactAssetType;
  assetId: string;
  assetLabel: string;
  sectionKey: string;
  /** Deterministic classification. Never derived from `confidence` in the UI. */
  impact: ImpactLevel;
  rationale: string;
  citations: ImpactCitation[];
  /** Lexical overlap with the changed obligation. Evidence, not a decision. */
  confidence: number;
  verificationState: ImpactVerificationState;
  verifiedBy: string | null;
  verifiedAt: string | null;
  createdAt: string;
}

export interface ImpactAssessment {
  id: string;
  organizationId: string;
  regulationVersionId: string;
  diffId: string | null;
  title: string;
  status: ImpactAssessmentStatus;
  method: RegulatoryAnalysisMethod;
  summary: string;
  /** Changed sections nothing on file matched. Absence of an item, not safety. */
  unmatchedSections: string[];
  itemCount: number;
  model: string;
  analyzerVersion: string;
  promptVersion: string;
  disclaimerVersion: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface ImpactAssessmentDetail extends ImpactAssessment {
  items: ImpactItem[];
}

export interface ImpactVerification {
  id: string;
  organizationId: string;
  impactItemId: string;
  actorId: string;
  decision: ImpactVerificationDecision;
  fromState: ImpactVerificationState;
  toState: ImpactVerificationState;
  rationale: string;
  decidedAt: string;
}

export interface RemediationAssignment {
  id: string;
  organizationId: string;
  sourceType: RemediationSourceType;
  sourceId: string;
  title: string;
  notes: string;
  assigneeId: string;
  dueAt: string | null;
  status: RemediationStatus;
  lifecycleTaskId: string | null;
  workflowInstanceId: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface RemediationOverdueRunResult {
  overdue: number;
}

export interface RegisterRegulationInput {
  regulationKey: string;
  name: string;
  jurisdiction?: string;
  authority?: string;
  description?: string;
}

export interface RegulationSectionInput {
  sectionKey: string;
  title: string;
  body?: string;
}

export interface CreateRegulationVersionInput {
  versionLabel: string;
  effectiveDate?: string | null;
  publishedDate?: string | null;
  sections?: RegulationSectionInput[];
}

export interface AddRegulationSourceInput {
  sourceKind: RegulationSourceKind;
  documentId?: string | null;
  referenceUrl?: string | null;
  citation?: string;
}

export interface CreateImpactAssessmentInput {
  fromVersionId: string;
  toVersionId: string;
  title: string;
  method?: RegulatoryAnalysisMethod;
}

export interface DecideImpactItemInput {
  decision: ImpactVerificationDecision;
  rationale?: string;
}

export interface AssignRemediationInput {
  title: string;
  notes?: string;
  assigneeId: string;
  dueAt?: string | null;
}

export interface CreateRemediationInput extends AssignRemediationInput {
  sourceType: RemediationSourceType;
  sourceId: string;
}

export interface RemediationFilter {
  status?: RemediationStatus;
  assigneeId?: string;
  sourceType?: RemediationSourceType;
}

/**
 * Every regulatory call the Studio UI makes, through the Worker proxy.
 *
 * Method names mirror the backend resource families rather than the screens,
 * so a screen cannot invent a route and the route-to-permission mapping at the
 * edge stays legible: reads need `policy_ai.governance.read`, writes need
 * `policy_ai.regulations.manage`, and only the item decision needs
 * `policy_ai.impact.verify`.
 */
export class RegulatoryClient {
  private http: GovernanceHttp;

  constructor(opts: GovernanceClientOptions) {
    this.http = new GovernanceHttp(opts);
  }

  // ----- regulation registry ------------------------------------------------
  regulations(orgId: string) {
    return this.http.get<{ regulations: Regulation[] }>(orgId, "regulations");
  }
  regulation(orgId: string, regulationId: string) {
    return this.http.get<{ regulation: Regulation }>(orgId, `regulations/${regulationId}`);
  }
  /** Registers a regulation the tenant owns. Odingard supplies no text. */
  registerRegulation(orgId: string, input: RegisterRegulationInput) {
    return this.http.post<{ regulation: Regulation }>(orgId, "regulations", input);
  }

  // ----- versions, sections and sources -------------------------------------
  versions(orgId: string, regulationId: string) {
    return this.http.get<{ versions: RegulationVersion[] }>(
      orgId,
      `regulations/${regulationId}/versions`,
    );
  }
  createVersion(orgId: string, regulationId: string, input: CreateRegulationVersionInput) {
    return this.http.post<{ version: RegulationVersion; sections: RegulationSection[] }>(
      orgId,
      `regulations/${regulationId}/versions`,
      input,
    );
  }
  /** Only a draft activates, and activating supersedes the previous active one. */
  activateVersion(orgId: string, versionId: string) {
    return this.http.post<{ version: RegulationVersion }>(
      orgId,
      `regulation-versions/${versionId}/activate`,
      {},
    );
  }
  sections(orgId: string, versionId: string) {
    return this.http.get<{ sections: RegulationSection[] }>(
      orgId,
      `regulation-versions/${versionId}/sections`,
    );
  }
  /** Replaces the draft's sections wholesale and recomputes the content hash. */
  importSections(orgId: string, versionId: string, sections: RegulationSectionInput[]) {
    return this.http.post<{ sections: RegulationSection[] }>(
      orgId,
      `regulation-versions/${versionId}/sections`,
      { sections },
    );
  }
  sources(orgId: string, versionId: string) {
    return this.http.get<{ sources: RegulationSource[] }>(
      orgId,
      `regulation-versions/${versionId}/sources`,
    );
  }
  addSource(orgId: string, versionId: string, input: AddRegulationSourceInput) {
    return this.http.post<{ source: RegulationSource }>(
      orgId,
      `regulation-versions/${versionId}/sources`,
      input,
    );
  }

  // ----- deterministic diff -------------------------------------------------
  /**
   * Section-level diff between two versions of one regulation. A POST because
   * the diff is stored, but it is a pure function of both content hashes: the
   * same pair returns the same row rather than a second one.
   */
  diff(orgId: string, fromVersionId: string, toVersionId: string) {
    return this.http.post<{ diff: RegulationDiff }>(
      orgId,
      `regulation-versions/${fromVersionId}/diff/${toVersionId}`,
      {},
    );
  }

  // ----- impact assessments -------------------------------------------------
  assessments(orgId: string, regulationVersionId?: string) {
    return this.http.get<{ assessments: ImpactAssessment[] }>(
      orgId,
      `impact-assessments${queryString({ regulationVersionId })}`,
    );
  }
  assessment(orgId: string, assessmentId: string) {
    return this.http.get<{ assessment: ImpactAssessmentDetail }>(
      orgId,
      `impact-assessments/${assessmentId}`,
    );
  }
  /** Items always come back `unverified`, whichever method produced them. */
  createAssessment(orgId: string, input: CreateImpactAssessmentInput) {
    return this.http.post<{ assessment: ImpactAssessmentDetail }>(
      orgId,
      "impact-assessments",
      input,
    );
  }
  /**
   * Moves an assessment through draft, in review and its terminal states.
   * Acceptance is refused while any item is still unverified, and accepted or
   * dismissed is final: there is no reopen route, so no reopen action exists.
   */
  setAssessmentStatus(orgId: string, assessmentId: string, status: ImpactAssessmentStatus) {
    return this.http.post<{ assessment: ImpactAssessment }>(
      orgId,
      `impact-assessments/${assessmentId}/status`,
      { status },
    );
  }

  // ----- impact items -------------------------------------------------------
  /**
   * Refused with 409 when the caller authored the assessment and is trying to
   * verify its own item. That rule is not mirrored here; the refusal is shown
   * in the backend's own words next to the item that produced it.
   */
  decideItem(orgId: string, itemId: string, input: DecideImpactItemInput) {
    return this.http.post<{ item: ImpactItem; verification: ImpactVerification }>(
      orgId,
      `impact-items/${itemId}/decision`,
      input,
    );
  }
  itemHistory(orgId: string, itemId: string) {
    return this.http.get<{ history: ImpactVerification[] }>(
      orgId,
      `impact-items/${itemId}/history`,
    );
  }
  /** Only a verified item may be assigned; an unverified one is a 409. */
  assignItem(orgId: string, itemId: string, input: AssignRemediationInput) {
    return this.http.post<{ assignment: RemediationAssignment }>(
      orgId,
      `impact-items/${itemId}/assign`,
      input,
    );
  }

  // ----- remediation --------------------------------------------------------
  assignments(orgId: string, filter: RemediationFilter = {}) {
    return this.http.get<{ assignments: RemediationAssignment[] }>(
      orgId,
      `remediation${queryString({ ...filter })}`,
    );
  }
  createAssignment(orgId: string, input: CreateRemediationInput) {
    return this.http.post<{ assignment: RemediationAssignment }>(orgId, "remediation", input);
  }
  setAssignmentStatus(orgId: string, assignmentId: string, status: RemediationStatus) {
    return this.http.post<{ assignment: RemediationAssignment }>(
      orgId,
      `remediation/${assignmentId}/status`,
      { status },
    );
  }
  /** Idempotent: an assignment is chased once, so a repeat run reports zero. */
  runOverdue(orgId: string) {
    return this.http.post<{ result: RemediationOverdueRunResult }>(
      orgId,
      "remediation/overdue/run",
      {},
    );
  }
}
